// Rug Room Shots: "Add a room" dialog (Rooms tab, SPEC 10).
// Uploads one room photo (multipart POST /rooms). The backend straightens it, stores it privately
// under s3://safavieh-rug-room-shots/rooms/ (never in safaviehimages) and creates the room as
// Proposed, source=manual, pinned by the admin, with the next free code from
// config.manualRoomNumberFrom (51) up to 99 for its 7-character prefix. "Activate now" then calls
// the existing POST rooms/{code}/status, so the usual activation rules (and re-planning) apply.
// The code shown before saving comes from GET rooms/next-code and is only a preview: POST returns
// the code actually saved.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Alert, CircularProgress,
  Stack, Typography, FormControlLabel, Checkbox, Box,
} from '@mui/material';
import { MdAddPhotoAlternate } from 'react-icons/md';
import rrsApi from './api';
import { primaryButtonSx, fmtInt, errText, styleName, shapeName } from './common';
import { ROOM_LETTERS } from './roomRules';

// 7th character of a room code, as in Peter's codes: the room type (L · Living room (accent chair), E · Entry, ...).
// The names come from /config roomLetters (fallback ROOM_LETTERS); the room-type rules use them (build spec 3.1).
const ACCEPT = 'image/jpeg,image/png,image/webp';
const SERVER_MESSAGE_CODES = ['worker_not_ready', 'room_numbers_full', 'storage_not_ready', 'busy', 'room_code_busy'];

const fmtMb = (n) => `${(Number(n || 0) / 1048576).toFixed(1)} MB`;

const AddRoomDialog = ({ open, onClose, config, matrix, notify, onAdded }) => {
  const styles = (config && config.styles) || [];
  const shapes = (config && config.shapes) || [];
  const features = (config && config.features) || {};
  const maxBytes = Number(config && config.maxRoomUploadBytes) || 41943040;
  const maxMb = Math.round(maxBytes / 1048576);
  const numberFrom = Number(config && config.manualRoomNumberFrom) || 51;
  const letterList = (config && Array.isArray(config.roomLetters) && config.roomLetters.length) ? config.roomLetters : ROOM_LETTERS;

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { url, w, h }
  const [styleCode, setStyleCode] = useState('');
  const [typeCode, setTypeCode] = useState('');
  const [letterSel, setLetterSel] = useState('L');
  const [otherLetter, setOtherLetter] = useState('');
  const [note, setNote] = useState('');
  const [activate, setActivate] = useState(true);
  const [nextCode, setNextCode] = useState(null); // { roomCode, prefix, number }
  const [codeError, setCodeError] = useState('');
  const [numbersFull, setNumbersFull] = useState(false); // 409 room_numbers_full for this prefix
  const [codeLoading, setCodeLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const urlRef = useRef(null);
  const codeSeq = useRef(0);

  const letter = letterSel === 'other' ? otherLetter : letterSel;
  const letterOk = /^[A-Z]$/.test(letter);
  const tooBig = !!(file && file.size > maxBytes);

  // Object URLs are revoked on every change of photo and when the dialog closes.
  const revokePreview = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    revokePreview();
    setFile(null);
    setPreview(null);
    setStyleCode('');
    setTypeCode('');
    setLetterSel('L');
    setOtherLetter('');
    setNote('');
    setActivate(true);
    setNextCode(null);
    setCodeError('');
    setNumbersFull(false);
    setError('');
    setBusy(false);
  }, [revokePreview]);

  // GET rooms/next-code (a preview only). Older answers are dropped when the inputs change again.
  const loadNextCode = useCallback(async (s, t, l) => {
    const my = codeSeq.current + 1;
    codeSeq.current = my;
    setNextCode(null);
    setCodeError('');
    setNumbersFull(false);
    if (!s || !t || !/^[A-Z]$/.test(l)) { setCodeLoading(false); return; }
    setCodeLoading(true);
    try {
      const res = await rrsApi.nextRoomCode(s, t, l);
      if (my === codeSeq.current) setNextCode(res || null);
    } catch (e) {
      if (my === codeSeq.current) {
        setCodeError((e.body && e.body.error) || errText(e));
        setNumbersFull(!!(e.body && e.body.code === 'room_numbers_full'));
      }
    } finally {
      if (my === codeSeq.current) setCodeLoading(false);
    }
  }, []);

  useEffect(() => { if (open) reset(); }, [open, reset]);
  useEffect(() => () => revokePreview(), [revokePreview]);
  useEffect(() => {
    if (!open) return;
    loadNextCode(styleCode, typeCode, letter);
  }, [open, styleCode, typeCode, letter, loadNextCode]);

  const pickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    revokePreview();
    setError('');
    setFile(f);
    setPreview(null);
    const url = URL.createObjectURL(f);
    urlRef.current = url;
    const img = new Image();
    img.onload = () => { if (urlRef.current === url) setPreview({ url, w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { if (urlRef.current === url) setPreview({ url: null, w: null, h: null }); };
    img.src = url;
  };

  const close = () => {
    if (busy) return;
    revokePreview();
    onClose();
  };

  const cell = (matrix && (matrix.cells || []).find((c) => c.style === styleCode && c.shape === typeCode)) || null;
  const waiting = cell ? Number(cell.gapTargets) || 0 : 0;

  const submit = async () => {
    if (!file || tooBig || !styleCode || !typeCode || !letterOk || busy) return;
    setBusy(true);
    setError('');
    let room = null;
    let res = null;
    try {
      res = await rrsApi.addRoom({ file, styleCode, typeCode, letter, note: note.trim() });
      room = (res && res.room) || null;
    } catch (e) {
      const c = e && e.body ? e.body.code : null;
      if (c === 'duplicate_image') {
        const cur = e.body.current;
        const code = (cur && (cur.roomCode || (typeof cur === 'string' ? cur : null))) || '';
        setError(`This photo is already room ${code}.`);
      } else if (e && e.status === 413) {
        setError(`The photo is larger than ${maxMb} MB.`);
      } else if (SERVER_MESSAGE_CODES.includes(c)) {
        setError(e.body.error || errText(e));
      } else {
        setError(errText(e));
      }
      setBusy(false);
      return;
    }

    const code = (room && room.roomCode) || (nextCode && nextCode.roomCode) || '';
    let msg = `Added ${code}.`;
    let variant = 'success';
    if (activate && code) {
      try {
        // No note: the backend keeps the room's note when this is blank (COALESCE), so the note typed in this
        // dialog survives. The room_status_changed event already records the activation.
        const st = await rrsApi.setRoomStatus(code, { status: 'active', note: '' });
        const n = (st && st.replannedTargets) || 0;
        msg = `Added ${code} and activated it${n ? `; ${fmtInt(n)} target(s) re-planning` : ''}.`;
      } catch (e) {
        msg = `Added ${code}, but activating it failed: ${(e.body && e.body.error) || errText(e)}. It is listed as Proposed.`;
        variant = 'warning';
      }
    }
    notify(msg, variant);
    ((res && res.warnings) || []).forEach((w) => { if (w && w.message) notify(w.message, 'warning'); });
    setBusy(false);
    revokePreview();
    if (onAdded) onAdded(room);
    onClose();
  };

  const canSubmit = !!file && !tooBig && !!styleCode && !!typeCode && letterOk && !busy && !numbersFull
    && features.addRoom !== false;

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle>Add a room</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Upload a room photo that already shows a rug of the right shape on the floor. Gemini swaps that rug for each
          product. Square photos work best (results are 1:1). JPG, PNG or WebP, up to {maxMb} MB and 60 megapixels;
          the short side must be at least 512 px. The photo is straightened, stored privately for this tool and never
          published to safaviehimages.
        </Alert>
        {features.addRoom === false && features.addRoomReason !== 'loading' && (
          <Alert severity="info" sx={{ mb: 2 }}>The worker has not been updated for page-added rooms yet.</Alert>
        )}

        {/* Photo */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={pickFile} />
          <Button variant="outlined" startIcon={<MdAddPhotoAlternate />} onClick={() => inputRef.current && inputRef.current.click()} disabled={busy}>
            Choose photo…
          </Button>
          {file && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{file.name}</Typography>
              <Typography variant="caption" color={tooBig ? 'error.main' : 'text.secondary'}>
                {fmtMb(file.size)}
                {preview && preview.w ? ` · ${preview.w} x ${preview.h}` : ''}
                {preview && !preview.url ? ' · this browser cannot preview it' : ''}
              </Typography>
            </Box>
          )}
        </Stack>
        {tooBig && <Alert severity="error" sx={{ mb: 2 }}>The photo is larger than {maxMb} MB.</Alert>}
        {preview && preview.url && (
          <Box
            component="img" src={preview.url} alt="Room photo"
            sx={{ display: 'block', maxWidth: '100%', maxHeight: 260, mx: 'auto', mb: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}
          />
        )}

        <Stack spacing={2}>
          <TextField select size="small" label="Style" value={styleCode} onChange={(e) => setStyleCode(e.target.value)}>
            {styles.map((s) => <MenuItem key={s.code} value={s.code}>{s.name} ({s.code})</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Room type" value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
            {shapes.map((s) => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={1.5}>
            <TextField
              select size="small" label="Room type (letter)" value={letterSel} sx={{ minWidth: 260 }}
              onChange={(e) => setLetterSel(e.target.value)}
              helperText="7th character of the code, as in Peter's codes. The room-type rules use it (e.g. large rounds go to D · Dining)."
            >
              {letterList.map((l) => <MenuItem key={l.code} value={l.code}>{l.code} · {l.name}</MenuItem>)}
              <MenuItem value="other">Other…</MenuItem>
            </TextField>
            {letterSel === 'other' && (
              <TextField
                size="small" label="Letter" value={otherLetter} sx={{ width: 90 }}
                onChange={(e) => setOtherLetter(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(-1))}
                error={otherLetter.length > 0 && !letterOk}
                inputProps={{ maxLength: 1, style: { fontFamily: 'monospace', textTransform: 'uppercase' } }}
              />
            )}
          </Stack>

          <Box>
            {codeLoading && <CircularProgress size={16} />}
            {!codeLoading && nextCode && nextCode.roomCode && (
              <Typography variant="body2">
                Will be saved as <strong style={{ fontFamily: 'monospace' }}>{nextCode.roomCode}</strong> (next free number
                from {numberFrom}; confirmed on save).
              </Typography>
            )}
            {!codeLoading && codeError && <Alert severity="warning">{codeError}</Alert>}
          </Box>

          <TextField
            size="small" label="Note (optional)" value={note} multiline minRows={2}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
          />

          <Box>
            <FormControlLabel
              control={<Checkbox checked={activate} onChange={(e) => setActivate(e.target.checked)} />}
              label="Activate now"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
              {waiting && styleCode && typeCode
                ? `Makes it available to plans right away and re-plans ${fmtInt(waiting)} waiting ${styleName(styleCode, styles)} × ${shapeName(typeCode, shapes)} rug(s).`
                : 'Makes it available to new plans right away.'}
            </Typography>
          </Box>
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit} sx={primaryButtonSx}>
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Add room'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRoomDialog;
